import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Heading,
  Heading2,
  Type,
  Image,
  MousePointerClick,
  Minus,
  FileText,
  GripVertical,
  Trash2,
  AppWindow,
} from 'lucide-react';

const BLOCK_ICONS: Record<string, React.ElementType> = {
  header: Heading,
  subtitle: Heading2,
  text: Type,
  image: Image,
  cta: MousePointerClick,
  divider: Minus,
  footer: FileText,
  window: AppWindow,
};

const BLOCK_LABELS: Record<string, string> = {
  header: 'Cabeçalho',
  subtitle: 'Subtítulo (H2)',
  text: 'Texto',
  image: 'Imagem',
  cta: 'Botão CTA',
  divider: 'Divisor',
  footer: 'Rodapé',
  window: 'Janela retrô',
};

function stripHtml(s: string): string {
  return s
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getBlockPreview(block: any): string {
  switch (block.type) {
    case 'header':
      return block.title || '(sem título)';
    case 'subtitle':
      return block.text || '(sem texto)';
    case 'text':
    case 'footer': {
      const plain = block.content ? stripHtml(block.content) : '';
      return plain ? plain.slice(0, 60) + (plain.length > 60 ? '…' : '') : '(sem conteúdo)';
    }
    case 'image':
      return block.url ? block.url.slice(0, 50) + '…' : '(sem URL)';
    case 'cta':
      return block.text || '(sem texto)';
    case 'divider':
      return `Espessura: ${block.thickness || 1}px`;
    case 'window':
      return block.title || '(sem título)';
    default:
      return '';
  }
}

interface SortableBlockProps {
  block: any;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}

function SortableBlock({ block, isSelected, onSelect, onRemove }: SortableBlockProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = BLOCK_ICONS[block.type] || Type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-3 rounded-lg border-2 bg-white cursor-pointer transition-colors ${
        isDragging ? 'opacity-40 shadow-xl' : ''
      } ${isSelected ? 'border-blue-500 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
      onClick={onSelect}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
        title="Arrastar para reordenar"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <div
        className={`p-1.5 rounded-md flex-shrink-0 ${
          isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
        }`}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-700">
          {BLOCK_LABELS[block.type] || block.type}
        </p>
        <p className="text-xs text-gray-400 truncate">{getBlockPreview(block)}</p>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
        title="Remover bloco"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

interface BlockCanvasProps {
  blocks: any[];
  selectedBlockId: string | null;
  onSelectBlock: (id: string) => void;
  onRemoveBlock: (id: string) => void;
}

export function BlockCanvas({
  blocks,
  selectedBlockId,
  onSelectBlock,
  onRemoveBlock,
}: BlockCanvasProps) {
  const { setNodeRef, isOver } = useDroppable({ id: 'block-canvas' });

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col gap-2 min-h-full p-4 rounded-xl border-2 transition-colors ${
        isOver ? 'border-blue-400 bg-blue-50' : 'border-dashed border-gray-200 bg-gray-50'
      }`}
    >
      <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
        {blocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <Type className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-400">Canvas vazio</p>
            <p className="text-xs text-gray-300 mt-1">Arraste blocos da paleta para começar</p>
          </div>
        ) : (
          blocks.map((block) => (
            <SortableBlock
              key={block.id}
              block={block}
              isSelected={selectedBlockId === block.id}
              onSelect={() => onSelectBlock(block.id)}
              onRemove={() => onRemoveBlock(block.id)}
            />
          ))
        )}
      </SortableContext>
    </div>
  );
}

export default BlockCanvas;
